import { useEffect, useRef } from 'react';
import { useSnackbar } from 'notistack';
import { useLocation } from 'react-router-dom';
import { Box, Button, Stack, Typography, alpha } from '@mui/material';
import Iconify from '../Iconify';
import useVersionCheck from '../../hooks/useVersionCheck';
import isPublicRoute from '../../utils/isPublicRoute';

/**
 * Component that monitors for:
 * 1. New app versions - shows persistent notification when a new deployment is detected
 * 2. Offline status - shows notification when user loses internet connection
 */
export default function VersionUpdateNotifier() {
    const { hasNewVersion, refreshApp, dismissUpdate, isOffline } = useVersionCheck();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const { pathname } = useLocation();
    const versionSnackbarRef = useRef(null);
    const offlineSnackbarRef = useRef(null);
    const wasOfflineRef = useRef(isOffline); // Track previous offline state

    // Handle new version notification
    useEffect(() => {
        // Suppress on patient-facing public pages
        if (isPublicRoute(pathname)) return;

        if (hasNewVersion && !versionSnackbarRef.current) {
            const key = enqueueSnackbar('version-update-notification', {
                persist: true,
                preventDuplicate: true,
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'center',
                },
                content: (snackbarId) => (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            py: 1.5,
                            px: 2.5,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            boxShadow: '0 8px 32px rgba(102, 126, 234, 0.35)',
                            color: '#fff',
                            minWidth: 320,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                bgcolor: alpha('#fff', 0.2),
                                flexShrink: 0,
                            }}
                        >
                            <Iconify icon="mdi:rocket-launch" width={22} />
                        </Box>

                        <Stack spacing={0.25} sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Update Available
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                A new version is ready to install
                            </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1}>
                            <Button
                                size="small"
                                onClick={() => {
                                    closeSnackbar(snackbarId);
                                    versionSnackbarRef.current = null;
                                    dismissUpdate();
                                }}
                                sx={{
                                    color: '#fff',
                                    opacity: 0.8,
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    '&:hover': {
                                        opacity: 1,
                                        bgcolor: alpha('#fff', 0.1),
                                    },
                                }}
                            >
                                Later
                            </Button>
                            <Button
                                size="small"
                                onClick={refreshApp}
                                startIcon={<Iconify icon="mdi:refresh" width={16} />}
                                sx={{
                                    bgcolor: '#fff',
                                    color: '#667eea',
                                    fontWeight: 700,
                                    fontSize: '0.75rem',
                                    px: 2,
                                    '&:hover': {
                                        bgcolor: alpha('#fff', 0.9),
                                    },
                                }}
                            >
                                Refresh
                            </Button>
                        </Stack>
                    </Box>
                ),
            });
            versionSnackbarRef.current = key;
        }

        // Close snackbar if version was dismissed or no longer has new version
        if (!hasNewVersion && versionSnackbarRef.current) {
            closeSnackbar(versionSnackbarRef.current);
            versionSnackbarRef.current = null;
        }
    }, [hasNewVersion, enqueueSnackbar, closeSnackbar, refreshApp, dismissUpdate, pathname]);

    // Handle offline/online notifications
    useEffect(() => {
        // Suppress on patient-facing public pages
        if (isPublicRoute(pathname)) return;

        const wasOffline = wasOfflineRef.current;

        // Went offline: show offline notification
        if (isOffline && !wasOffline) {
            const key = enqueueSnackbar('offline-notification', {
                persist: true,
                preventDuplicate: true,
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'center',
                },
                content: () => (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            py: 1.25,
                            px: 2,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                            boxShadow: '0 8px 32px rgba(245, 158, 11, 0.35)',
                            color: '#fff',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                bgcolor: alpha('#fff', 0.2),
                            }}
                        >
                            <Iconify icon="eva:alert-triangle-fill" width={18} />
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            You&apos;re offline — Some features may not work
                        </Typography>
                    </Box>
                ),
            });
            offlineSnackbarRef.current = key;
        }

        // Came back online: close offline notification and show success
        if (!isOffline && wasOffline) {
            // Close offline snackbar if it exists
            if (offlineSnackbarRef.current) {
                closeSnackbar(offlineSnackbarRef.current);
                offlineSnackbarRef.current = null;
            }

            // Show "back online" message briefly - use setTimeout to ensure it appears after closing
            setTimeout(() => {
                enqueueSnackbar('back-online-notification', {
                    autoHideDuration: 3000,
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'center',
                    },
                    content: () => (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                py: 1.25,
                                px: 2,
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                boxShadow: '0 8px 32px rgba(16, 185, 129, 0.35)',
                                color: '#fff',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    bgcolor: alpha('#fff', 0.2),
                                }}
                            >
                                <Iconify icon="eva:checkmark-circle-2-fill" width={18} />
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                You&apos;re back online!
                            </Typography>
                        </Box>
                    ),
                });
            }, 100);
        }

        // Update the ref for next comparison
        wasOfflineRef.current = isOffline;
    }, [isOffline, enqueueSnackbar, closeSnackbar, pathname]);

    return null;
}
